<script setup lang="ts">
  import { computed, useId } from 'vue';
  import { getModelFromContext, setModelWithContext, useFormContext } from '@othercommerce/foundation';

  interface Option {
    label: string,
    value: any,
    available?: number,
  }

  interface Props {
    id?: string | undefined;
    name?: string | undefined;
    label?: string | undefined;
    required?: boolean;
    readonly?: boolean;
    disabled?: boolean;
    options?: Option[];
  }

  defineOptions({
    inheritAttrs: false,
  });

  const props = withDefaults(defineProps<Props>(), {
    required: false,
    readonly: false,
    disabled: false,
    options: () => [],
  });

  const context = useFormContext();
  const random = useId();

  const model = defineModel({
    get: (value) => getModelFromContext(props.name, context, value ?? []),
    set: (value) => setModelWithContext(props.name, context, value ?? []),
    default: () => [],
  });

  const id = computed(() => {
    return props.id ?? random;
  });

  const disabled = computed(() => {
    // Checkboxes may have only a disabled state, for compatibility we treat a readonly as disabled here.
    return Boolean(props.disabled || props.readonly || (context && context.readonly.value));
  });
</script>
<template>
  <SfControlWrapper v-bind="{ id, name, label, required }" v-slot="{errorId}">
    <fieldset :aria-labelledby="name" :aria-describedby="errorId">
      <label class="checkbox-container" v-for="option in options" :key="option.value" :for="id + '-' + option.value">
        <input type="checkbox" class="checkbox"
          :id="id + '-' + option.value"
          :name="name"
          :disabled="disabled || (option.available != null && option.available <= 0)"
          :value="option.value"
          v-model="model"
          v-bind="{...$attrs}"
        />
        <span class="checkmark" />
        <span v-if="option.label" class="checkbox-label">
          <span>{{ option.label }}</span>
          <small class="available" v-if="option.available != null">({{ option.available }})</small>
        </span>
      </label>
    </fieldset>
  </SfControlWrapper>
</template>
<style scoped lang="scss">

  fieldset {
    border: 0;
    min-width: 0;
  }

  .checkbox-container {
    display: block;
    position: relative;
    cursor: pointer;
    user-select: none;
    padding-left: calc(var(--sf-checkbox-line-height) * 1.5);
    margin: calc(var(--sf-checkbox-line-height) / 2) 0;
    text-decoration: underline;
    text-decoration-color: transparent;
    transition: text-decoration-color 0.2s ease-out;

    .checkbox {
      position: absolute;
      opacity: 0;
      height: 0;
      width: 0;

      &:focus-visible {
        ~ .checkmark {
          background-color: var(--sf-input-background-focus);
          border-color: var(--sf-input-border-focus);
          outline: 2px solid var(--sf-input-border-focus);
          outline-offset: 2px;
        }

        ~ .checkbox-label {
          text-decoration: underline;
        }
      }

      &:active ~ .checkmark {
        background-color: var(--sf-input-background-active);
        border-color: var(--sf-input-border-active);
      }

      &:checked ~ .checkmark:after {
        display: block;
      }

      &:disabled {
        & ~ .checkmark {
          background-color: var(--sf-input-background-disabled);
          border-color: var(--sf-input-border-disabled);
          pointer-events: none;
          cursor: not-allowed;

          &:after {
            background-color: var(--sf-input-border-disabled);
          }
        }

        & ~ .checkbox-label {
          color: var(--sf-input-color-disabled);
          pointer-events: none;
          cursor: not-allowed;
        }
      }
    }

    .checkmark {
      position: absolute;
      top: 0;
      left: 0;
      padding: 0;
      height: var(--sf-checkbox-line-height);
      width: var(--sf-checkbox-line-height);
      border: 1px solid var(--sf-checkbox-checkmark-border-color);
      border-radius: var(--sf-checkbox-checkmark-border-radius);

      &:after {
        content: "";
        position: absolute;
        top: 50%;
        left: 50%;
        display: none;
        transform: translate(-50%, -50%);
        height: calc(var(--sf-checkbox-line-height) - 4px);
        width: calc(var(--sf-checkbox-line-height) - 4px);
        background-color: var(--sf-checkbox-checkmark-background-color);
        border-radius: var(--sf-checkbox-checkmark-check-border-radius);
      }
    }

    .checkbox-label {
      display: flex;
      font-size: var(--sf-checkbox-label-font-size);
      line-height: var(--sf-checkbox-line-height);
      align-items: center;
      text-transform: var(--sf-checkbox-label-text-transform);
      color: var(--sf-checkbox-label-color);
      gap: .375rem;

      .available {
        color: var(--sf-checkbox-label-available-color);
        font-size: var(--sf-checkbox-label-available-font-size);
      }
    }

    @media(hover: hover) {
      &:hover {
        text-decoration-color: inherit;
      }
    }
  }
</style>
